import React, { useState, useEffect } from "react";
import categoryData from "../../api/categories";
import { formatPage } from "../../utils/formatPage";
import { motion, AnimatePresence } from "framer-motion";
import "./OffcanvasMenu.css";
import { useTranslation } from "react-i18next";
import ErrorImage from "../../assets/error.png";

// Default image for categories without products
const DEFAULT_CATEGORY_IMAGE = ErrorImage;

// Special image only for "All Products"
const ALL_PRODUCTS_IMAGE = "https://apimukemel.com/api/assets/mukemel.png";

// Cache duration: 1 hour (in milliseconds)
const CACHE_DURATION = 60 * 60 * 1000;
const CACHE_KEY = "menuProductsCache";
const CACHE_TIME_KEY = "menuProductsCacheTime";

export default function OffcanvasMenu({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [categoriesWithProducts, setCategoriesWithProducts] = useState([]);

  // Fetch products when menu opens (with caching)
  useEffect(() => {
    if (isOpen && products.length === 0) {
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Build category images map and filter categories with products
  useEffect(() => {
    if (products.length > 0) {
      const imageMap = {};
      const validCategories = [];

      // Set "All Products" special image
      imageMap["all"] = ALL_PRODUCTS_IMAGE;

      // Get random product image for each category
      categoryData.category.forEach((cat) => {
        const categoryProducts = products.filter((p) =>
          p.category?.some(
            (c) => c.toLowerCase() === cat.categoryname.toLowerCase(),
          ),
        );

        if (categoryProducts.length > 0) {
          // Category has products - add to valid categories and use random product image
          validCategories.push(cat);
          const randomCatProduct =
            categoryProducts[
              Math.floor(Math.random() * categoryProducts.length)
            ];
          imageMap[cat.categoryname] =
            randomCatProduct?.default_images?.[0] ||
            randomCatProduct?.color_images?.[0]?.images?.[0] ||
            DEFAULT_CATEGORY_IMAGE;
        }
        // Categories without products are NOT added to validCategories
      });

      setCategoryImages(imageMap);
      setCategoriesWithProducts(validCategories);
    }
  }, [products]);

  const loadProducts = async () => {
    try {
      // Check if cached data exists and is still valid
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheTime = localStorage.getItem(CACHE_TIME_KEY);
      
      if (cachedData && cacheTime) {
        const timeDiff = Date.now() - parseInt(cacheTime, 10);
        if (timeDiff < CACHE_DURATION) {
          // Use cached data
          const parsedData = JSON.parse(cachedData);
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            setProducts(parsedData);
            return;
          }
        }
      }

      // Fetch fresh data from API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products`);
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setProducts(data);
        // Cache the data
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  if (!isOpen) return null;

  const handleCategoryClick = (categoryName) => {
    if (categoryName === "all") {
      window.location.href = "/shop";
    } else if (categoryName === "Perfume") {
      window.location.href = "/category/perfume";
    } else {
      window.location.href = `/category/${formatPage(categoryName)}`;
    }
  };

  // Combine "All Products" with categories that have products
  const allCategories = [
    { id: "all", categoryname: "all", translationKey: "All Products" },
    ...categoriesWithProducts,
  ];

  return (
    <>
      <motion.div
        initial={{ x: -500, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -500, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="offcanvas offcanvas-start show menu-offcanvas"
        style={{
          width: "450px",
          maxWidth: "100vw",
          visibility: "visible",
          zIndex: 1045,
          backgroundColor: "white",
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title px-3 fw-light txtcolor">
            {t("Menu")}
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              setSelectedCategory(null);
              onClose();
            }}
          ></button>
        </div>

        <div className="offcanvas-body p-3">
          <div className="category-grid">
            {allCategories.map((cat) => (
              <motion.div
                key={cat.id}
                className="category-card"
                onClick={() => handleCategoryClick(cat.categoryname)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="category-card-image">
                  <img
                    src={
                      categoryImages[cat.categoryname] || DEFAULT_CATEGORY_IMAGE
                    }
                    alt={cat.categoryname}
                    loading="lazy"
                  />
                </div>
                <div className="category-card-overlay">
                  <span className="category-card-title">
                    {t(cat.translationKey) || cat.categoryname}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {selectedCategory && (
          <motion.div
            initial={{ x: 500, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 500, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="offcanvas offcanvas-start show"
            style={{
              width: "450px",
              left: "450px",
              visibility: "visible",
              backgroundColor: "white",
              zIndex: 1050,
            }}
          >
            <div className="border-bottom">
              <img
                src={selectedCategory.categorybanner}
                alt={selectedCategory.categoryname}
                className="w-100 mb-0 pt-0"
              />
              <h5 className="offcanvas-title py-3 text-center txtcolor">
                {selectedCategory.categoryname}
              </h5>
            </div>

            <div className="offcanvas-body ">
              {selectedCategory.subcategories.length > 0 ? (
                selectedCategory.subcategories.map((sub) => (
                  <a
                    key={sub.id}
                    href={`/${formatPage(
                      selectedCategory.categoryname,
                    )}/${formatPage(sub.name)}`}
                    className="submenu-link  d-block pb-0 fs-6 mb-2 mx-2 txtcolor"
                  >
                    <span className="menu-arrow mt-1">&gt;</span>
                    <span className="underline-wrapper mx-3">
                      {sub.name}
                      <span className="underline"></span>
                    </span>
                  </a>
                ))
              ) : (
                <p className="mt-3 text-muted text-center">Alt kategori yok</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 1040,
        }}
        onClick={() => {
          setSelectedCategory(null);
          onClose();
        }}
      ></div>
    </>
  );
}
